import store from '@/store/store'
import { moduleMapper } from '@/store/helpers/modules.map'
/* i18n */
import i18n from '@/assets/setups/i18n'
/* Assets & Helpers */
import { APP_ANDROID_VERSION, APP_IOS_VERSION, MDW_ENDPOINT, MDW_PORTAL_ENDPOINT } from '@/assets/constants/core'
import { buildUrlParams } from '@/network/helpers/urlBuilders'

/* AUTHENTICATED REQUESTS */
export const mobileClient = ({ method, url, data, params, filePath, fileName }) => {
	const completeUrl = MDW_ENDPOINT + generateCompleteURl(url, params)

    if (process.env.VUE_APP_ENV !== 'PRD') {
        console.log('[NATIVE METHOD]', method)
        console.log('[NATIVE URL]', completeUrl)
        console.log('[NATIVE DATA]', data)
        console.log('[NATIVE PARAMS]', params)
        if (filePath) console.log('[NATIVE FILE_PATH]', filePath)
        if (fileName) console.log('[NATIVE FILE_NAME]', fileName)
    }

	return requestBuilder(method, completeUrl, data, filePath, fileName)
}

/* AUTHENTICATED REQUESTS */
export const mobileClientDownloadValidator = ({ method, url, filePath }) => {
	return requestBuilder(method, url, null, filePath)
}

export const mobileClientPortal = ({ method, url, data, params, filePath, fileName }) => {
	const completeUrl = MDW_PORTAL_ENDPOINT + generateCompleteURl(url, params, true)

    if (process.env.VUE_APP_ENV !== 'PRD') {
        console.log('[NATIVE METHOD]', method)
        console.log('[NATIVE URL]', completeUrl)
        console.log('[NATIVE DATA]', data)
        console.log('[NATIVE PARAMS]', params)
        if (filePath) console.log('[NATIVE FILE_PATH]', filePath)
        if (fileName) console.log('[NATIVE FILE_NAME]', fileName)
    }
	return requestBuilder(method, completeUrl, data, filePath, fileName)
}

/* CREATING OUR COMPLETE URL FOR REQUESTS */
export const generateCompleteURl = (url, params, nonAutenticated) => {
	// Query Params add
	const tempParams = params || {}
	// Default values needed in all requests
	tempParams.noCacheHelper = new Date().getTime()

	tempParams.clientApp = store.getters.getDeviceInUse === 'android' ? 'Android' : 'ios'
	tempParams.appVersion = store.getters.getDeviceInUse === 'android' ? APP_ANDROID_VERSION : APP_IOS_VERSION

	if (!nonAutenticated) tempParams.token = store.getters['session/getToken']

	const queryParams = buildUrlParams(tempParams).toString()
	const parsedUrl = fixUrlSpecialChars(url)
	/* Adding queryParams */
	if (parsedUrl[0] === '/') {
		return `${parsedUrl.replace('/', '')}?${queryParams}`
	} else {
		return `${parsedUrl}?${queryParams}`
	}
}

/* DYNAMIC NATIVE REQUEST BUILDER */
export const requestBuilder = (method, completeUrl, data, filePath, fileName) => {
	/* Since most of our API's in axios dont need to define the method, because default method is GET
	we need this validation to act as axios and define the default method */
	if (method === undefined) method = 'GET'
	/* Since native request on PUT method needs data, we need to make this validation to send empty object on data so that the request goes trough */
	if ((method === 'PUT' || method === 'POST' || method === 'DELETE' || method === 'downloadFile') && data === undefined) data = {}
	/*
        Normaly we dont need to set headers because by default { 'Content-Type': 'application/json' } is the header, but,
        on DELETE methods the plugin will give error 415 if we dont hard-set header to { 'Content-Type': 'application/json' },
        so to resolve any issues we do this validation to send empty header by default but on method DELETE request e set it to { 'Content-Type': 'application/json' }
    */
	let headers = {}
	if (method === 'DELETE') headers = { 'Content-Type': 'application/json' }
	if (data) {
		if (data.clientChannelDevice) {
            headers = {
                ...headers,
                'X-INM-Device-System': data.clientChannelDevice.system,
                'X-INM-Device-Id': data.clientChannelDevice.id
            }

            if (data.clientChannelDevice.vendor) headers = { ...headers, 'X-INM-Device-Vendor': data.clientChannelDevice.vendor }
            if (data.clientChannelDevice.model) headers = { ...headers, 'X-INM-Device-Model': data.clientChannelDevice.model }
            if (data.clientChannelDevice.systemVersion) headers = { ...headers, 'X-INM-Device-System-Version': data.clientChannelDevice.systemVersion }
        }
	}

	/* PROMISE */
	const promise = new Promise((resolve, reject) => {
        if (method === 'uploadFile') {
            window.cordova.plugin.http.uploadFile(
                completeUrl,
                data, // Data for GET PUT POST, Params for DELETE
                headers, // headers
                filePath,
                fileName,
                /* SUCCESS FUNCTION CALLBACK */
                response => {
                    resolve(sucessCallbackHandler(response, method))
                },
                /* ERROR FUNCTION CALLBACK */
                response => {
                    reject(errorCallbackHangler(response, method))
                }
            )
        } else if (method === 'downloadFile') {
            window.cordova.plugin.http.downloadFile(
                completeUrl,
                data, // params for this downloadFile
                headers,
                filePath,
                /* SUCCESS FUNCTION CALLBACK */
                response => {
                    console.log('Download successful: File saved to ' + response.nativeURL)
                    resolve(sucessCallbackHandler(response, method))
                },
                /* ERROR FUNCTION CALLBACK */
                response => {
                    reject(errorCallbackHangler(response, method))
                }
            )
        } else {
            window.cordova.plugin.http[method.toLowerCase()](
                completeUrl,
                data, // Data for GET PUT POST, Params for DELETE
                headers, // headers
                /* SUCCESS FUNCTION CALLBACK */
                response => {
                    resolve(sucessCallbackHandler(response, method))
                },
                /* ERROR FUNCTION CALLBACK */
                response => {
                    reject(errorCallbackHangler(response, method))
                }
            )
        }
	})

	return promise
}

const sucessCallbackHandler = (response, method) => {
    if (response.data & response.data === '') {
        response.data = undefined
    } else {
        try {
            response.data = JSON.parse(response.data)
        } catch (e) {
            response.data = undefined
        }
    }
    /* Updating session time @store */
    if (response.data && response.data.remainingSecondsForSessionExpire) {
        store.dispatch(`${[moduleMapper.SESSION]}/updateRemainingSecondsForSessionExpire`, response.data.remainingSecondsForSessionExpire)
    }
    if (response.data && response.data.challenge) {
        store.dispatch('showTokenChallengeModal', { response: response.data })
    }
    if (process.env.VUE_APP_ENV !== 'PRD') {
        console.log(`[${method} SUCCESS]`, response.data)
    }
    return response
}

const errorCallbackHangler = (response, method) => {
    if (process.env.VUE_APP_ENV !== 'PRD') {
        console.log(`[${method} ERROR]`, response)
    }

    let tempData
    try {
        tempData = response.error ? JSON.parse(response.error) : undefined
    } catch (e) {
        if (process.env.VUE_APP_ENV !== 'PRD') {
            console.log(e)
        }
        tempData = {
            errorLevel: 'ERROR',
            inDegradedMode: false,
            message: i18n.global.t('genericErrorMessage')
        }
    }
    /*
    * We need to use this const to copy the way Axios provides error.response to our views,
    * or else our current way to handle error cases will have to be changed
    */
    const axiosTypeResponse = {
        response: {
            data: tempData,
            headers: response.headers,
            status: response.status,
            url: response.url
        }
    }
    if (axiosTypeResponse.data & response.error === '') {
        axiosTypeResponse.data = undefined
    } else {
        try {
            // Not necessary anymore
        } catch (e) {
            axiosTypeResponse.data = undefined
        }
    }

    if (process.env.VUE_APP_ENV !== 'PRD') {
        console.log(axiosTypeResponse, 'axios like response')
    }

    if (axiosTypeResponse.response && axiosTypeResponse.response.status === 401) {
        if (axiosTypeResponse.response && axiosTypeResponse.response.data?.code === 'token_not_found') {
            store.dispatch(`${[moduleMapper.SESSION]}/forceLogout`)
            window.sessionStorage.setItem('showLoginAlert', axiosTypeResponse.response.data.code)
        } else if (axiosTypeResponse.response && axiosTypeResponse.response.data?.code === 'expired_token') {
            store.dispatch(`${[moduleMapper.SESSION]}/updateSessionExpireState`, true)
        }
        return axiosTypeResponse
    } else {
        return axiosTypeResponse
    }
}

const fixUrlSpecialChars = completeUrl => {
    let parsedUrl = completeUrl
    if (parsedUrl.includes('Á')) parsedUrl = parsedUrl.replaceAll('Á', '%C3%81')
    if (parsedUrl.includes(' ')) parsedUrl = parsedUrl.replaceAll(' ', '%20')
    return parsedUrl
}
